import {
    CSSProperties,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import {HistoryContext} from '../../contexts/history'
import {Button, CircularProgress, Collapse, MenuItem, TextField} from '@mui/material'
import {getCurrentColors} from '../../utils/colors'
import {RadarFornecedorApiService} from '../../services/RadarFornecedorApi.service'
import {
    AutuacaoAmbiental,
    ConsultaDetail,
    ConsultaOut,
    ContratacaoFornecedor,
    FornecedorCandidato,
    MencaoMidia,
    MencaoOficial,
    RadarFornecedor,
    Sancao,
    SinalIrregularidade,
    Socio,
    Veredito,
} from '../../types/radarFornecedor'

const api = new RadarFornecedorApiService()
// Sem polling: 1 carga inicial e, daqui em diante, o andamento chega só por PUSH do notifier
// (websocket). Mesmo padrão do analisador (R.E.D), do gerador e da pesquisa de preços —
// diretriz do CTO de parar o polling agressivo no backend.
const STATUS_FINAIS = ['CONCLUIDO', 'ERRO']
const POR_PAGINA = 15

const COLORS = getCurrentColors()

function darken(hex: string, f: number): string {
    const m = (hex || '').replace('#', '').match(/.{2}/g)
    if (!m || m.length !== 3) return '#0b7d6b'
    const v = m.map((x) => Math.max(0, Math.round(parseInt(x, 16) * (1 - f))))
    return '#' + v.map((x) => x.toString(16).padStart(2, '0')).join('')
}

// Identidade visual da marca (teal/verde). Adapta a white-label via getCurrentColors.
const VERDE = COLORS.secundariaEscura || '#3acdb4'
const VERDE_ESCURO = darken(VERDE, 0.45)

const VEREDITO_UI: Record<Veredito, {label: string; cor: string; fundo: string; emoji: string}> = {
    APTO: {label: 'Regular', cor: '#15803d', fundo: '#dcfce7', emoji: '🟢'},
    ATENCAO: {label: 'Atenção', cor: '#b45309', fundo: '#fef3c7', emoji: '🟡'},
    IMPEDIDO: {label: 'Impedimento identificado', cor: '#b91c1c', fundo: '#fee2e2', emoji: '🔴'},
    INDEFINIDO: {label: 'Indefinido', cor: '#475569', fundo: '#f1f5f9', emoji: '⚪'},
}

const NIVEL_COR: Record<string, string> = {ALTO: '#b91c1c', MEDIO: '#b45309', BAIXO: '#475569'}

const FONTE_SANCAO_LABEL: Record<string, string> = {
    CEIS: 'CEIS',
    CNEP: 'CNEP',
    CEPIM: 'CEPIM',
    LENIENCIA: 'Acordo de leniência (CGU)',
    TCU_INIDONEOS: 'Inidôneos do TCU',
    LISTA_SUJA: 'Trabalho análogo a escravo (MTE)',
}

const SENT_UI: Record<string, {emoji: string; cor: string; rotulo: string}> = {
    NEGATIVO: {emoji: '🔻', cor: '#b91c1c', rotulo: 'tom negativo'},
    POSITIVO: {emoji: '🔺', cor: '#15803d', rotulo: 'tom positivo'},
    NEUTRO: {emoji: '•', cor: '#64748b', rotulo: 'tom neutro'},
}

const TIPS = [
    {
        emoji: '⚖️',
        cor: '#b91c1c',
        titulo: 'Nem toda sanção impede contratar',
        texto: 'A inidoneidade (art. 156, IV) impede com toda a Administração; a suspensão restringe-se ao órgão sancionador (TCU, Acórdão 2243/2013-Plenário). Verifique o tipo.',
    },
    {
        emoji: '🔎',
        cor: VERDE_ESCURO,
        titulo: 'Diligência prévia é dever do gestor',
        texto: 'O TCU entende que cabe à Administração verificar a idoneidade do fornecedor antes de contratar, sob pena de responsabilização do gestor.',
    },
    {
        emoji: '🧾',
        cor: '#1d4ed8',
        titulo: 'Conflito de interesse é vedação legal',
        texto: 'O art. 14 da Lei 14.133 veda a participação de empresa cujo dirigente seja agente público do órgão contratante.',
    },
    {
        emoji: '🆕',
        cor: '#7c3aed',
        titulo: 'Empresa recém-criada pede atenção',
        texto: 'CNPJ aberto há pouco tempo vencendo contratos relevantes merece checagem de capacidade técnica e experiência — um ponto de atenção, não uma irregularidade.',
    },
    {
        emoji: '🚫',
        cor: '#be123c',
        titulo: 'CNPJ irregular na Receita',
        texto: 'Empresa baixada, inapta ou suspensa na Receita Federal não tem aptidão para ser contratada.',
    },
    {
        emoji: '💸',
        cor: '#0f766e',
        titulo: 'Sanções de até 6 anos',
        texto: 'As penalidades da Lei 14.133 vão de advertência à declaração de inidoneidade, por até 6 anos (art. 156).',
    },
    {
        emoji: '📊',
        cor: '#0f766e',
        titulo: 'Preço de referência é obrigatório',
        texto: 'A pesquisa de preços (art. 23 da Lei 14.133 e IN SEGES 65/2021) deve usar pelo menos 3 fontes e tratar os preços fora da curva antes de definir o valor estimado.',
    },
    {
        emoji: '📄',
        cor: '#1d4ed8',
        titulo: 'Habilitação tem documentação própria',
        texto: 'Regularidade fiscal (CND, FGTS), trabalhista (CNDT), qualificação técnica e econômico-financeira são exigências dos arts. 62 a 70 da Lei 14.133.',
    },
    {
        emoji: '🤝',
        cor: '#7c3aed',
        titulo: 'ME e EPP têm tratamento favorecido',
        texto: 'A LC 123/2006 garante preferência e regularização posterior de documentos fiscais a microempresas e empresas de pequeno porte.',
    },
    {
        emoji: '🗂️',
        cor: VERDE_ESCURO,
        titulo: 'Planejamento vem antes',
        texto: 'ETP e Termo de Referência estruturam a contratação: descrevem a necessidade, a solução e os requisitos antes de ir ao mercado (art. 18 da Lei 14.133).',
    },
    {
        emoji: '🏛️',
        cor: '#b45309',
        titulo: 'Concentração não é, por si só, irregularidade',
        texto: 'Vencer muito em um único órgão pode ser legítimo — é um ponto de atenção que merece olhar para a competitividade do certame, não uma acusação.',
    },
]

function formatCnpj(v: string): string {
    const d = (v || '').replace(/\D/g, '').slice(0, 14)
    return d
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2')
}

function brl(v?: number | null): string {
    if (typeof v !== 'number') return '—'
    return v.toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'})
}

function fmtData(d?: string | null): string {
    if (!d || d.length < 10) return d || '—'
    const [a, m, dia] = d.slice(0, 10).split('-')
    return `${dia}/${m}/${a}`
}

// Valida o dígito verificador do CNPJ (ignora pontuação). Só é chamado quando a entrada
// parece um CNPJ — nome continua indo para a busca por nome.
function cnpjValido(v: string): boolean {
    const d = (v || '').replace(/\D/g, '')
    if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false
    const dv = (base: string) => {
        let soma = 0
        let peso = base.length - 7
        for (const ch of base) {
            soma += parseInt(ch, 10) * peso
            peso = peso === 2 ? 9 : peso - 1
        }
        const r = soma % 11
        return r < 2 ? 0 : 11 - r
    }
    const d1 = dv(d.slice(0, 12))
    return d1 === +d[12] && dv(d.slice(0, 12) + d1) === +d[13]
}

const LINK_RECEITA =
    'https://solucoes.receita.fazenda.gov.br/Servicos/cnpjreva/Cnpjreva_Solicitacao.asp'
const linkTransparencia = (cnpj: string) =>
    `https://portaldatransparencia.gov.br/busca?termo=${cnpj}`
// Conferência manual dos diários (cobertura do robô é parcial): busca pelo nome.
const buscaQueridoDiario = (termo: string) =>
    `https://queridodiario.ok.org.br/pesquisa?termo=${encodeURIComponent(termo)}`
const buscaDOU = (termo: string) =>
    `https://www.in.gov.br/consulta/-/buscar/dou?q=${encodeURIComponent('"' + termo + '"')}`
// TCU não tem API aberta gratuita: link para a consulta oficial em tempo real.
const LINK_TCU =
    'https://portal.tcu.gov.br/carta-de-servicos/certidoes/lista-de-licitantes-inidoneos'

// Certidões obrigatórias na habilitação (art. 68, Lei 14.133). Sem API aberta gratuita
// (captcha): link direto aos portais oficiais para o gestor emitir/conferir.
const CERTIDOES = [
    {
        nome: 'CND Federal (Receita/PGFN)',
        desc: 'Tributos federais e dívida ativa da União',
        url: 'https://www.gov.br/pt-br/servicos/emitir-certidao-de-regularidade-fiscal-perante-a-fazenda-nacional',
    },
    {
        nome: 'FGTS — CRF (Caixa)',
        desc: 'Regularidade do FGTS',
        url: 'https://consulta-crf.caixa.gov.br/consultacrf/pages/consultaEmpregador.jsf',
    },
    {
        nome: 'CNDT (TST)',
        desc: 'Débitos trabalhistas',
        url: 'https://cndt-certidao.tst.jus.br/inicio.faces',
    },
]

const card: CSSProperties = {
    border: '1px solid #e2e8f0',
    borderRadius: 14,
    padding: 22,
    background: '#fff',
    marginBottom: 18,
    boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
}

const linkStyle: CSSProperties = {color: VERDE_ESCURO, fontWeight: 600, textDecoration: 'none'}

export default function RadarPanel() {
    const [fase, setFase] = useState<'inicio' | 'escolha' | 'relatorio'>('inicio')
    const [origem, setOrigem] = useState<'inicio' | 'escolha'>('inicio')
    const [termo, setTermo] = useState('')
    const [periodo, setPeriodo] = useState(24)
    const [buscando, setBuscando] = useState(false)
    const [candidatos, setCandidatos] = useState<FornecedorCandidato[]>([])
    const [buscaFeita, setBuscaFeita] = useState(false)
    const [detalhe, setDetalhe] = useState<ConsultaDetail | null>(null)
    const [carregando, setCarregando] = useState(false)
    const [erro, setErro] = useState<string | null>(null)
    const [exportando, setExportando] = useState(false)
    const [recentes, setRecentes] = useState<ConsultaOut[]>([])
    // Push do notifier (websocket) é o único canal de progresso; pendenteRef = consulta aguardada.
    const {radarSocketMessage} = useContext(HistoryContext)
    const pendenteRef = useRef<string | null>(null)

    const carregarRecentes = useCallback(async () => {
        try {
            const r = await api.listarConsultas(8, 0)
            setRecentes(r.consultas || [])
        } catch {
            /* best-effort: recentes não bloqueia a tela */
        }
    }, [])

    useEffect(() => {
        carregarRecentes()
    }, [carregarRecentes])

    // Carga (1 request): busca a ficha atual da consulta. Sem repetição — quem avisa de
    // mudança é o push do notifier (useEffect abaixo).
    const carregarDetalhe = async (id: string) => {
        try {
            const d = await api.getConsulta(id)
            setDetalhe(d)
            if (STATUS_FINAIS.includes(d.status)) setCarregando(false)
        } catch {
            setCarregando(false)
            setErro('Falha ao consultar o andamento.')
        }
    }

    // PUSH do notifier: chegou o status final da consulta que aguardamos → recarrega a ficha
    // na hora. Push puro, sem polling — mesmo padrão do analisador, gerador e pesquisa de preços.
    useEffect(() => {
        const msg = (radarSocketMessage || {}) as {consulta_id?: string; status?: string}
        if (!msg.consulta_id || msg.consulta_id !== pendenteRef.current) return
        if (msg.status === 'CONCLUIDO' || msg.status === 'ERRO') {
            carregarDetalhe(msg.consulta_id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [radarSocketMessage])

    const iniciarConsulta = async (cnpj: string, nome?: string, de: 'inicio' | 'escolha' = 'inicio') => {
        setOrigem(de)
        setErro(null)
        setDetalhe(null)
        setCarregando(true)
        setFase('relatorio')
        try {
            const consulta = await api.criarConsulta(cnpj, periodo, nome)
            pendenteRef.current = consulta.id // aguarda o push do notifier p/ esta consulta
            carregarDetalhe(consulta.id) // estado inicial (PENDENTE/COLETANDO)
        } catch (e: any) {
            setCarregando(false)
            setErro(
                e?.response?.data?.detail ||
                    'Não foi possível iniciar a consulta. Verifique se você está logado e se o serviço está no ar.'
            )
        }
    }

    const abrirConsulta = async (id: string) => {
        setOrigem('inicio')
        setErro(null)
        setDetalhe(null)
        setCarregando(true)
        setFase('relatorio')
        try {
            const d = await api.getConsulta(id)
            setDetalhe(d)
            if (STATUS_FINAIS.includes(d.status)) setCarregando(false)
            else pendenteRef.current = id // ainda em andamento: aguarda o push do notifier
        } catch {
            setCarregando(false)
            setErro('Não foi possível abrir a consulta.')
        }
    }

    const buscarNome = async (nome: string) => {
        setErro(null)
        setBuscando(true)
        setBuscaFeita(false)
        setCandidatos([])
        setFase('escolha')
        try {
            const r = await api.buscarPorNome(nome)
            setCandidatos(r.candidatos || [])
        } catch {
            setErro('Não foi possível buscar por nome agora. Tente o CNPJ.')
        } finally {
            setBuscando(false)
            setBuscaFeita(true)
        }
    }

    const consultar = () => {
        const t = termo.trim()
        const digitos = t.replace(/\D/g, '')
        const pareceCnpj = /^[\d.\/\-\s]+$/.test(t) && digitos.length > 0 // só números/pontuação
        if (pareceCnpj) {
            if (digitos.length !== 14 || !cnpjValido(digitos)) {
                setErro('CNPJ inválido — confira os 14 dígitos (com ou sem pontuação).')
                return
            }
            iniciarConsulta(digitos)
        } else if (t.length >= 3) {
            buscarNome(t)
        } else {
            setErro('Digite um CNPJ válido ou o nome da empresa (mín. 3 letras).')
        }
    }

    const novaConsulta = () => {
        pendenteRef.current = null
        setFase('inicio')
        setDetalhe(null)
        setCandidatos([])
        setErro(null)
        setTermo('')
        carregarRecentes()
    }

    const voltar = () => {
        setErro(null)
        if (fase === 'relatorio' && origem === 'escolha') {
            setDetalhe(null)
            setFase('escolha')
        } else {
            novaConsulta()
        }
    }

    const radar: RadarFornecedor | null = detalhe?.resultado ?? null

    const exportarPdf = async () => {
        if (!detalhe?.id) return
        setExportando(true)
        try {
            const blob = await api.exportarPdf(detalhe.id)
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `radar-${(radar?.razao_social || detalhe.cnpj || 'fornecedor').slice(0, 40)}.pdf`
            document.body.appendChild(a)
            a.click()
            a.remove()
            window.URL.revokeObjectURL(url)
        } catch {
            setErro('Não foi possível gerar o PDF agora.')
        } finally {
            setExportando(false)
        }
    }

    return (
        <div style={{flex: 1, overflowY: 'auto', background: '#f1f5f9'}}>
            <BarraBusca
                inicio={fase === 'inicio'}
                termo={termo}
                setTermo={setTermo}
                periodo={periodo}
                setPeriodo={setPeriodo}
                onConsultar={consultar}
            />

            <div style={{maxWidth: 960, margin: '0 auto', padding: '0 24px 40px'}}>
                {fase !== 'inicio' && (
                    <button onClick={voltar} aria-label="Voltar" style={backBtn}>
                        ← Voltar
                    </button>
                )}

                {erro && (
                    <div style={{...card, borderColor: '#fecaca', background: '#fef2f2', color: '#b91c1c'}}>
                        {erro}
                    </div>
                )}

                {fase === 'inicio' && <Landing recentes={recentes} onAbrir={abrirConsulta} />}

                {fase === 'escolha' && (
                    <Escolha
                        termo={termo}
                        buscando={buscando}
                        buscaFeita={buscaFeita}
                        candidatos={candidatos}
                        onEscolher={(c) => {
                            setTermo(formatCnpj(c.cnpj))
                            iniciarConsulta(c.cnpj, c.razao_social, 'escolha')
                        }}
                    />
                )}

                {fase === 'relatorio' && (
                    <>
                        {carregando && !radar && <CarregandoComDicas status={detalhe?.status} />}
                        {detalhe?.status === 'ERRO' && (
                            <div style={{...card, borderColor: '#fecaca', background: '#fef2f2', color: '#b91c1c'}}>
                                A consulta falhou: {detalhe.error_message || 'erro desconhecido'}
                            </div>
                        )}
                        {radar && (
                            <Ficha
                                radar={radar}
                                onExportarPdf={exportarPdf}
                                exportando={exportando}
                                criadoEm={detalhe?.finished_at || detalhe?.created_at || null}
                                onAtualizar={() => iniciarConsulta(radar.cnpj)}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

/* ----------------------------- Barra de busca ----------------------------- */

function BarraBusca(props: {
    inicio: boolean
    termo: string
    setTermo: (v: string) => void
    periodo: number
    setPeriodo: (v: number) => void
    onConsultar: () => void
}) {
    const ehCnpj = props.termo.replace(/\D/g, '').length === 14
    return (
        <div
            style={{
                background: `linear-gradient(135deg, ${VERDE_ESCURO} 0%, ${VERDE_ESCURO} 45%, ${VERDE} 135%)`,
                padding: props.inicio ? '52px 24px 60px' : '22px 24px',
                color: '#fff',
                transition: 'padding 0.3s',
            }}
        >
            <div style={{maxWidth: 960, margin: '0 auto'}}>
                {props.inicio && (
                    <div style={{textAlign: 'center', marginBottom: 26}}>
                        <div style={{fontSize: 44, marginBottom: 6}}>🛰️</div>
                        <h1 style={{margin: 0, fontSize: 34, fontWeight: 800, letterSpacing: -0.5}}>
                            Radar de Fornecedor
                        </h1>
                        <p style={{margin: '8px auto 0', maxWidth: 560, opacity: 0.92, fontSize: 16}}>
                            Antes de homologar ou contratar, conheça o fornecedor: identidade,
                            sócios, histórico no PNCP e sanções — com segurança jurídica.
                        </p>
                    </div>
                )}
                <div
                    style={{
                        display: 'flex',
                        gap: 10,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        background: '#fff',
                        padding: 10,
                        borderRadius: 14,
                        boxShadow: '0 10px 30px rgba(2,44,38,0.22)',
                        maxWidth: props.inicio ? 720 : '100%',
                        margin: '0 auto',
                    }}
                >
                    <TextField
                        value={props.termo}
                        onChange={(e) => {
                            const v = e.target.value
                            const digitos = v.replace(/\D/g, '')
                            props.setTermo(
                                digitos.length >= 11 && /^[\d./-]*$/.test(v) ? formatCnpj(v) : v
                            )
                        }}
                        placeholder="CNPJ (00.000.000/0000-00) ou nome da empresa"
                        size="small"
                        style={{flex: 1, minWidth: 240}}
                        onKeyDown={(e) => e.key === 'Enter' && props.onConsultar()}
                    />
                    <TextField
                        select
                        label="Período"
                        value={props.periodo}
                        onChange={(e) => props.setPeriodo(Number(e.target.value))}
                        size="small"
                        style={{width: 130}}
                    >
                        {[6, 12, 18, 24].map((m) => (
                            <MenuItem key={m} value={m}>
                                {m} meses
                            </MenuItem>
                        ))}
                    </TextField>
                    <Button
                        variant="contained"
                        onClick={props.onConsultar}
                        style={{
                            background: VERDE_ESCURO,
                            height: 40,
                            textTransform: 'none',
                            fontWeight: 700,
                            paddingInline: 22,
                        }}
                    >
                        {ehCnpj ? 'Consultar' : 'Buscar'}
                    </Button>
                </div>
                <p
                    style={{
                        maxWidth: props.inicio ? 720 : '100%',
                        margin: '10px auto 0',
                        padding: '10px 14px',
                        background: '#fff',
                        borderRadius: 12,
                        boxShadow: '0 6px 18px rgba(2,44,38,0.12)',
                        fontSize: 12.5,
                        lineHeight: 1.5,
                        color: '#475569',
                        textAlign: 'center',
                    }}
                >
                    💡 A busca <strong style={{color: VERDE_ESCURO}}>mais certeira é pelo CNPJ</strong> —
                    traz todos os contratos da empresa. Pela razão social, localizamos a empresa pelos
                    contratos públicos, então pode não encontrar todas.
                </p>
            </div>
        </div>
    )
}

/* --------------------- Carregando (com dicas rotativas) ------------------- */

function CarregandoComDicas({status, mensagem}: {status?: string; mensagem?: string}) {
    const [i, setI] = useState(0)
    useEffect(() => {
        const id = setInterval(() => setI((p) => (p + 1) % TIPS.length), 4500)
        return () => clearInterval(id)
    }, [])
    const t = TIPS[i % TIPS.length]
    return (
        <div style={card}>
            <div style={{display: 'flex', gap: 14, alignItems: 'center'}}>
                <CircularProgress size={22} sx={{color: VERDE_ESCURO}} />
                <span style={{color: '#475569'}}>
                    {mensagem || 'Coletando contratos no PNCP, identidade na Receita e sanções…'}
                    {status ? ` (${status})` : ''}
                </span>
            </div>
            <div style={{marginTop: 16, padding: '12px 14px', borderLeft: `4px solid ${t.cor}`, background: '#f8fafc', borderRadius: 8}}>
                <div style={{fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.6, marginBottom: 4}}>
                    VOCÊ SABIA?
                </div>
                <div style={{fontWeight: 700, color: '#0f172a', marginBottom: 4}}>
                    {t.emoji} {t.titulo}
                </div>
                <div style={{color: '#475569', fontSize: 14, lineHeight: 1.5}}>{t.texto}</div>
            </div>
        </div>
    )
}

/* -------------------------------- Landing -------------------------------- */

function Landing({recentes, onAbrir}: {recentes: ConsultaOut[]; onAbrir: (id: string) => void}) {
    const passos = [
        {n: '1', t: 'Digite o CNPJ ou o nome', d: 'Pelo nome, você escolhe a empresa certa.'},
        {n: '2', t: 'O Radar cruza as fontes', d: 'PNCP, Receita Federal e sanções, em segundos.'},
        {n: '3', t: 'Receba o veredito', d: 'Identidade, sócios, histórico e 🟢🟡🔴 — auditável.'},
    ]
    return (
        <div style={{paddingTop: 28}}>
            <div
                style={{
                    background: '#fff',
                    borderRadius: 14,
                    padding: 22,
                    display: 'flex',
                    gap: 22,
                    flexWrap: 'wrap',
                    justifyContent: 'space-around',
                    textAlign: 'center',
                    marginBottom: 26,
                }}
            >
                {passos.map((p) => (
                    <div key={p.n} style={{maxWidth: 240}}>
                        <div
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                background: VERDE_ESCURO,
                                color: '#fff',
                                fontWeight: 800,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 8px',
                            }}
                        >
                            {p.n}
                        </div>
                        <div style={{fontWeight: 700, color: VERDE_ESCURO}}>{p.t}</div>
                        <div style={{color: '#64748b', fontSize: 13}}>{p.d}</div>
                    </div>
                ))}
            </div>

            {recentes.length > 0 && (
                <div style={{...card, marginBottom: 26}}>
                    <h3 style={{marginTop: 0, color: VERDE_ESCURO}}>Suas consultas recentes</h3>
                    {recentes.map((c) => {
                        const v = VEREDITO_UI[(c.veredito as Veredito) || 'INDEFINIDO'] ?? VEREDITO_UI.INDEFINIDO
                        return (
                            <button
                                key={c.id}
                                onClick={() => onAbrir(c.id)}
                                style={{...botaoLinha, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '12px 14px', marginTop: 8}}
                                onMouseEnter={(e) => (e.currentTarget.style.borderColor = VERDE)}
                                onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#e2e8f0')}
                            >
                                <div style={{textAlign: 'left', overflow: 'hidden'}}>
                                    <div style={{fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                        {c.razao_social || c.nome_informado || 'Fornecedor'}
                                    </div>
                                    <div style={{color: '#64748b', fontSize: 13}}>CNPJ {formatCnpj(c.cnpj)}</div>
                                </div>
                                <div style={{whiteSpace: 'nowrap', color: v.cor, fontWeight: 700, fontSize: 13}}>
                                    {c.status === 'CONCLUIDO'
                                        ? `${v.emoji} ${v.label}`
                                        : c.status === 'ERRO'
                                        ? '⚠️ erro'
                                        : '⏳ processando'}
                                </div>
                            </button>
                        )
                    })}
                </div>
            )}

            <h2 style={{color: VERDE_ESCURO, textAlign: 'center', marginBottom: 4}}>Você sabia?</h2>
            <p style={{textAlign: 'center', color: '#64748b', marginTop: 0, marginBottom: 22}}>
                Compliance e integridade nas contratações públicas — com base na Lei 14.133/2021 e
                na jurisprudência do TCU.
            </p>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: 16,
                }}
            >
                {TIPS.map((t, i) => (
                    <div
                        key={i}
                        style={{
                            background: '#fff',
                            borderRadius: 14,
                            padding: 20,
                            borderTop: `4px solid ${t.cor}`,
                            boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
                        }}
                    >
                        <div style={{fontSize: 28, marginBottom: 8}}>{t.emoji}</div>
                        <div style={{fontWeight: 700, color: '#0f172a', marginBottom: 6}}>{t.titulo}</div>
                        <div style={{color: '#475569', fontSize: 14, lineHeight: 1.5}}>{t.texto}</div>
                    </div>
                ))}
            </div>
        </div>
    )
}

/* ------------------------------- Escolha PJ ------------------------------ */

function Escolha(props: {
    termo: string
    buscando: boolean
    buscaFeita: boolean
    candidatos: FornecedorCandidato[]
    onEscolher: (c: FornecedorCandidato) => void
}) {
    if (props.buscando) {
        return (
            <div style={{paddingTop: 8}}>
                <CarregandoComDicas mensagem={`Buscando "${props.termo}" no PNCP…`} />
            </div>
        )
    }
    return (
        <div style={{paddingTop: 8}}>
            <div style={card}>
                <h3 style={{marginTop: 0, color: VERDE_ESCURO}}>
                    Empresas encontradas para "{props.termo}"
                </h3>
                {props.buscaFeita && props.candidatos.length === 0 && (
                    <div style={{color: '#64748b'}}>
                        Nenhuma empresa encontrada com esse nome no PNCP. Tente outro termo ou use o
                        CNPJ.
                    </div>
                )}
                {props.candidatos.map((c) => (
                    <button
                        key={c.cnpj}
                        onClick={() => props.onEscolher(c)}
                        style={{
                            ...botaoLinha,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 12,
                            padding: '14px 16px',
                            marginTop: 10,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.borderColor = VERDE)}
                        onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#e2e8f0')}
                    >
                        <div style={{textAlign: 'left'}}>
                            <div style={{fontWeight: 700, color: '#0f172a'}}>{c.razao_social}</div>
                            <div style={{color: '#64748b', fontSize: 13}}>CNPJ {formatCnpj(c.cnpj)}</div>
                        </div>
                        <div style={{textAlign: 'right', whiteSpace: 'nowrap'}}>
                            <div style={{fontWeight: 700, color: VERDE_ESCURO}}>{c.qtd_contratos}</div>
                            <div style={{color: '#94a3b8', fontSize: 12}}>contratos</div>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    )
}

/* --------------------------------- Ficha --------------------------------- */

function fmtDataHora(iso: string): string {
    try {
        return new Date(iso).toLocaleString('pt-BR', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        })
    } catch {
        return iso.slice(0, 10)
    }
}

function motivoVeredito(r: RadarFornecedor): string {
    if (r.veredito === 'IMPEDIDO')
        return 'Sanção que impede licitar/contratar com a Administração (inidoneidade — CEIS/TCU).'
    if (r.veredito === 'ATENCAO') {
        const m: string[] = []
        if (r.sancoes?.some((s) => s.fonte === 'LISTA_SUJA')) m.push('Lista Suja (trabalho escravo)')
        if (r.sancoes?.some((s) => !s.abrange_licitar && s.fonte !== 'LISTA_SUJA')) m.push('sanção de alcance restrito')
        if (r.situacao_cadastral && r.situacao_cadastral !== 'ATIVA') m.push(`situação ${r.situacao_cadastral} na Receita`)
        if (r.sinais?.some((s) => s.nivel === 'ALTO')) m.push('indício de risco alto')
        return 'Atenção: ' + (m.length ? m.join('; ') + '.' : 'há pontos a verificar.')
    }
    if (r.veredito === 'APTO') return 'Sem impedimentos nem indícios de alto risco encontrados.'
    return 'Dados insuficientes para concluir.'
}

function resumoLinha(r: RadarFornecedor): string {
    const p: string[] = []
    if (r.situacao_cadastral) p.push(r.situacao_cadastral)
    const h = r.historico
    if (h?.qtd_contratacoes) p.push(`${brl(h.total_adjudicado)} em ${h.qtd_contratacoes} contrato(s)`)
    const nSanc = r.sancoes?.length || 0
    p.push(
        r.sancoes?.some((s) => s.abrange_licitar)
            ? 'com sanção impeditiva'
            : nSanc
            ? `${nSanc} sanção(ões) não impeditiva(s)`
            : r.sancoes_verificadas
            ? 'sem sanções'
            : 'sanções não verificadas'
    )
    if (r.sinais?.length) p.push(`${r.sinais.length} alerta(s)`)
    return p.join(' · ')
}

function Ficha({
    radar,
    onExportarPdf,
    exportando,
    criadoEm,
    onAtualizar,
}: {
    radar: RadarFornecedor
    onExportarPdf: () => void
    exportando: boolean
    criadoEm: string | null
    onAtualizar: () => void
}) {
    const v = VEREDITO_UI[radar.veredito] ?? VEREDITO_UI.INDEFINIDO
    const h = radar.historico

    return (
        <div style={{paddingTop: 8}}>
            {/* Cabeçalho + veredito + ações */}
            <div style={card}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12}}>
                    <div>
                        <div style={{fontSize: 19, fontWeight: 800, color: '#0f172a'}}>
                            {radar.razao_social || 'Fornecedor'}
                            {radar.nome_fantasia ? (
                                <span style={{fontWeight: 500, color: '#64748b'}}> · {radar.nome_fantasia}</span>
                            ) : null}
                        </div>
                        <div style={{color: '#64748b', fontSize: 14, marginTop: 2}}>
                            CNPJ {formatCnpj(radar.cnpj)}
                            {radar.municipio ? ` · ${radar.municipio}/${radar.uf || ''}` : radar.uf ? ` · ${radar.uf}` : ''}
                            {radar.situacao_cadastral ? ' · Receita: ' : ''}
                            {radar.situacao_cadastral && (
                                <b style={{color: radar.situacao_cadastral === 'ATIVA' ? '#15803d' : '#b91c1c'}}>
                                    {radar.situacao_cadastral}
                                </b>
                            )}
                        </div>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'}}>
                        <div
                            title={motivoVeredito(radar)}
                            style={{
                                background: v.fundo,
                                color: v.cor,
                                fontWeight: 800,
                                padding: '10px 18px',
                                borderRadius: 999,
                                whiteSpace: 'nowrap',
                                fontSize: 15,
                                cursor: 'help',
                            }}
                        >
                            {v.emoji} {v.label}
                        </div>
                        <Button onClick={onAtualizar} variant="text" size="small" style={{textTransform: 'none', color: VERDE_ESCURO, minWidth: 0}}>
                            ↻ Atualizar
                        </Button>
                        <Button
                            onClick={onExportarPdf}
                            disabled={exportando}
                            variant="outlined"
                            size="small"
                            style={{textTransform: 'none', color: VERDE_ESCURO, borderColor: VERDE}}
                        >
                            {exportando ? 'Gerando…' : '⬇ Exportar PDF'}
                        </Button>
                    </div>
                </div>
                {/* Resumo executivo + carimbo de data */}
                <div style={{marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
                    <div style={{fontSize: 14, color: '#334155'}}>{resumoLinha(radar)}</div>
                    {criadoEm && (
                        <div style={{fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap'}}>
                            Consultado em {fmtDataHora(criadoEm)}
                        </div>
                    )}
                </div>
            </div>

            {/* Alertas honestos */}
            {radar.alertas?.length > 0 && (
                <div style={{...card, borderColor: '#fde68a', background: '#fffbeb'}}>
                    {radar.alertas.map((a, i) => (
                        <div key={i} style={{color: '#92400e', fontSize: 14, marginBottom: 4}}>
                            ⚠️ {a}
                        </div>
                    ))}
                </div>
            )}

            <IdentidadeCard radar={radar} />

            {/* Métricas */}
            <div style={card}>
                <h3 style={{marginTop: 0, color: VERDE_ESCURO}}>Histórico no PNCP</h3>
                <div style={{display: 'flex', gap: 28, flexWrap: 'wrap'}}>
                    <Metric label="Total adjudicado" value={brl(h.total_adjudicado)} />
                    <Metric label="Contratos" value={String(h.qtd_contratacoes)} />
                    <Metric label="Órgãos" value={String(h.qtd_orgaos)} />
                    <Metric
                        label="Concentração no maior órgão"
                        value={h.concentracao_top_orgao != null ? `${Math.round(h.concentracao_top_orgao * 100)}%` : '—'}
                    />
                </div>
                {h.top_objeto && (
                    <div style={{marginTop: 12, color: '#475569', fontSize: 14}}>
                        Objeto predominante: <b>{h.top_objeto}</b>
                        {h.ufs?.length ? ` · UFs: ${h.ufs.join(', ')}` : ''}
                    </div>
                )}
            </div>

            <SecaoContratos radar={radar} />

            {/* Alertas / indícios */}
            <div style={card}>
                <h3 style={{marginTop: 0, color: VERDE_ESCURO}}>Alertas e indícios</h3>
                {radar.sinais?.length ? (
                    radar.sinais.map((s: SinalIrregularidade, i) => (
                        <div
                            key={i}
                            style={{
                                borderLeft: `4px solid ${NIVEL_COR[s.nivel] || '#475569'}`,
                                padding: '8px 12px',
                                marginBottom: 10,
                                background: '#f8fafc',
                                borderRadius: 6,
                            }}
                        >
                            <div style={{fontWeight: 600, color: NIVEL_COR[s.nivel] || '#334155'}}>
                                {s.titulo} <span style={{fontSize: 11, fontWeight: 700}}>[{s.nivel}]</span>
                            </div>
                            <div style={{color: '#475569', fontSize: 13}}>{s.detalhe}</div>
                        </div>
                    ))
                ) : (
                    <div style={{color: '#64748b', fontSize: 14}}>Nenhum alerta no histórico.</div>
                )}
            </div>

            <Certidoes />

            <Sancoes radar={radar} />

            <SecaoDiarios radar={radar} />

            <SecaoMidia radar={radar} />

            <SecaoIbama radar={radar} />

            {/* Disclaimer (as fontes já estão distribuídas ao longo do relatório) */}
            <div style={{...card, background: '#f8fafc'}}>
                <div style={{fontSize: 12, color: '#64748b', lineHeight: 1.6}}>
                    Informações indicativas, coletadas de fontes públicas na data da consulta. Não
                    substituem a verificação oficial nem constituem juízo sobre idoneidade.
                </div>
            </div>
        </div>
    )
}

function IdentidadeCard({radar}: {radar: RadarFornecedor}) {
    const linhas: [string, ReactNode][] = []
    if (radar.cnae) linhas.push(['Atividade (CNAE)', radar.cnae])
    if (radar.cnaes_secundarios?.length)
        linhas.push(['Atividades secundárias', radar.cnaes_secundarios.join(' · ')])
    if (radar.natureza_juridica) linhas.push(['Natureza jurídica', radar.natureza_juridica])
    if (radar.porte) linhas.push(['Porte', radar.porte])
    if (typeof radar.capital_social === 'number') linhas.push(['Capital social', brl(radar.capital_social)])
    if (radar.data_abertura) linhas.push(['Abertura', fmtData(radar.data_abertura)])
    if (radar.municipio || radar.uf)
        linhas.push(['Município/UF', `${radar.municipio || ''}${radar.uf ? '/' + radar.uf : ''}`])
    if (radar.endereco)
        linhas.push([
            'Endereço (sede)',
            <>
                {radar.endereco}{' '}
                <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([radar.endereco, radar.municipio, radar.uf].filter(Boolean).join(', '))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={linkStyle}
                >
                    mapa ↗
                </a>
            </>,
        ])
    if (radar.cep) linhas.push(['CEP', radar.cep])
    if (radar.telefone)
        linhas.push([
            'Telefone',
            <a href={`tel:${radar.telefone.replace(/\D/g, '')}`} style={linkStyle}>
                {radar.telefone}
            </a>,
        ])
    if (radar.email)
        linhas.push([
            'E-mail',
            <a href={`mailto:${radar.email}`} style={linkStyle}>
                {radar.email}
            </a>,
        ])
    if (radar.optante_simples != null)
        linhas.push(['Simples Nacional', radar.optante_simples ? 'Optante' : 'Não optante'])

    return (
        <div style={card}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
                <h3 style={{margin: 0, color: VERDE_ESCURO}}>Identidade (Receita Federal)</h3>
                <div style={{fontSize: 13}}>
                    <a href={LINK_RECEITA} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                        Consultar na Receita ↗
                    </a>
                </div>
            </div>
            {linhas.length > 0 && (
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '6px 18px', marginTop: 12}}>
                    {linhas.map(([k, val], i) => (
                        <div key={i} style={{fontSize: 14, color: '#475569'}}>
                            <span style={{color: '#94a3b8'}}>{k}: </span>
                            <b style={{color: '#0f172a'}}>{val}</b>
                        </div>
                    ))}
                </div>
            )}
            {radar.socios?.length > 0 && <SociosLista socios={radar.socios} />}
        </div>
    )
}

function SociosLista({socios}: {socios: Socio[]}) {
    const [pagina, setPagina] = useState(1)
    const POR_PAG = 8
    const totalPaginas = Math.max(1, Math.ceil(socios.length / POR_PAG))
    const ini = (pagina - 1) * POR_PAG
    const itens = socios.slice(ini, ini + POR_PAG)
    return (
        <div style={{marginTop: 16}}>
            <div style={{fontWeight: 700, color: '#0f172a', marginBottom: 6, fontSize: 14}}>
                Sócios e administradores <span style={countStyle}>({socios.length})</span>
            </div>
            {itens.map((s: Socio, i) => (
                <div
                    key={ini + i}
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 12,
                        padding: '6px 0',
                        borderTop: i ? '1px solid #f1f5f9' : 'none',
                        fontSize: 13,
                    }}
                >
                    <span style={{color: '#0f172a', fontWeight: 600}}>{s.nome || '—'}</span>
                    <span style={{color: '#64748b'}}>
                        {s.qualificacao || ''}
                        {s.data_entrada ? ` · desde ${fmtData(s.data_entrada)}` : ''}
                    </span>
                </div>
            ))}
            {totalPaginas > 1 && (
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14, marginTop: 12}}>
                    <Button size="small" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)} style={{textTransform: 'none', color: VERDE_ESCURO}}>
                        ← Anterior
                    </Button>
                    <span style={{color: '#475569', fontSize: 13}}>
                        Página {pagina} de {totalPaginas}
                    </span>
                    <Button size="small" disabled={pagina >= totalPaginas} onClick={() => setPagina((p) => p + 1)} style={{textTransform: 'none', color: VERDE_ESCURO}}>
                        Próxima →
                    </Button>
                </div>
            )}
        </div>
    )
}

function Sancoes({radar}: {radar: RadarFornecedor}) {
    const hoje = new Date().toISOString().slice(0, 10)
    const vigente = (s: Sancao) => !s.data_fim || s.data_fim >= hoje
    // ativas (vigentes) primeiro; dentro disso, as impeditivas no topo
    const sancoes = [...(radar.sancoes || [])].sort(
        (a, b) => Number(vigente(b)) - Number(vigente(a)) || Number(!!b.abrange_licitar) - Number(!!a.abrange_licitar)
    )
    return (
        <div style={card}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
                <h3 style={{margin: 0, color: VERDE_ESCURO}}>
                    Sanções e impedimentos{' '}
                    {!radar.sancoes_verificadas && (
                        <span style={{fontSize: 12, color: '#b45309', fontWeight: 400}}>— não verificadas</span>
                    )}
                </h3>
                <div style={{fontSize: 13}}>
                    <a href={linkTransparencia(radar.cnpj)} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                        Portal da Transparência ↗
                    </a>{' '}·{' '}
                    <a href={LINK_TCU} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                        Inidôneos do TCU ↗
                    </a>
                </div>
            </div>
            <div style={{marginTop: 10}}>
                {sancoes.length ? (
                    sancoes.map((s, i) => {
                        const impede = !!s.abrange_licitar
                        const viva = vigente(s)
                        return (
                            <div key={i} style={{fontSize: 14, color: viva ? (impede ? '#b91c1c' : '#b45309') : '#94a3b8', marginBottom: 6}}>
                                {viva ? (impede ? '🔴' : '🟡') : '⚪'} {FONTE_SANCAO_LABEL[s.fonte] || s.fonte} — {s.descricao || 'sanção registrada'}
                                {s.data_fim
                                    ? viva
                                        ? ` · vigente até ${fmtData(s.data_fim)}`
                                        : ` · expirada em ${fmtData(s.data_fim)}`
                                    : ''}
                                {viva && !impede && <span style={{color: '#94a3b8'}}> · alcance restrito</span>}
                                {s.link && (
                                    <>
                                        {' '}
                                        <a href={s.link} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                                            evidência ↗
                                        </a>
                                    </>
                                )}
                            </div>
                        )
                    })
                ) : (
                    <div style={{color: '#64748b', fontSize: 14}}>
                        {radar.sancoes_verificadas
                            ? 'Nenhuma sanção ativa encontrada.'
                            : 'Confirme manualmente no Portal da Transparência até a checagem automática entrar no ar.'}
                    </div>
                )}
            </div>
        </div>
    )
}

function SecaoIbama({radar}: {radar: RadarFornecedor}) {
    const aut = radar.autuacoes_ambientais || []
    if (!radar.ibama_verificado && aut.length === 0) return null
    return (
        <div style={card}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
                <h3 style={{margin: 0, color: VERDE_ESCURO}}>
                    Ambiental — IBAMA <span style={countStyle}>({aut.length})</span>
                </h3>
                <a
                    href="https://servicos.ibama.gov.br/ctf/publico/areasembargadas/ConsultaPublicaAreasEmbargadas.php"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={linkStyle}
                >
                    Consulta IBAMA ↗
                </a>
            </div>
            <div style={{marginTop: 10}}>
                {aut.length ? (
                    <>
                        {aut.slice(0, 20).map((a: AutuacaoAmbiental, i) => (
                            <div key={i} style={{fontSize: 14, color: a.ativo ? '#b45309' : '#64748b', marginBottom: 6}}>
                                {a.ativo ? '🟠' : '⚪'} <b>{a.tipo}</b>
                                {a.descricao ? ` — ${a.descricao}` : ''}
                                {a.municipio ? ` · ${a.municipio}/${a.uf || ''}` : ''}
                                {a.data ? ` · ${fmtData(a.data)}` : ''}
                                {typeof a.valor_multa === 'number' ? ` · multa ${brl(a.valor_multa)}` : ''}
                                {!a.ativo && <span style={{color: '#94a3b8'}}> · regularizado/desembargado</span>}
                                {a.link && (
                                    <>
                                        {' '}
                                        <a href={a.link} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                                            ver ↗
                                        </a>
                                    </>
                                )}
                            </div>
                        ))}
                        <div style={fonteNota}>
                            Indício ambiental (autos/embargos) — não impede licitar por si só. Confira a situação de cada registro.
                        </div>
                    </>
                ) : (
                    <div style={{color: '#64748b', fontSize: 14}}>Nenhum auto/embargo ambiental localizado para este CNPJ.</div>
                )}
            </div>
        </div>
    )
}

function SecaoDiarios({radar}: {radar: RadarFornecedor}) {
    const ms = radar.noticias_oficiais || []
    const termo = radar.nome_busca || radar.razao_social || ''
    return (
        <div style={card}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
                <h3 style={{margin: 0, color: VERDE_ESCURO}}>
                    Menções em Diários Oficiais <span style={countStyle}>({ms.length})</span>
                </h3>
                {termo && (
                    <div style={{fontSize: 13}}>
                        <a href={buscaQueridoDiario(termo)} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                            Diários municipais/estaduais ↗
                        </a>{' '}·{' '}
                        <a href={buscaDOU(termo)} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                            DOU ↗
                        </a>
                    </div>
                )}
            </div>
            <div style={{marginTop: 10}}>
                {ms.length ? (
                    <>
                        <div style={avisoHomonimo}>
                            ⚠️ Resultados por nome — podem incluir homônimos. Confira o diário-fonte.
                        </div>
                        {ms.slice(0, 14).map((m: MencaoOficial, i) => (
                            <div key={i} style={{borderLeft: `3px solid ${VERDE}`, padding: '6px 12px', marginBottom: 8, background: '#f8fafc', borderRadius: 6}}>
                                <div style={{fontSize: 13, color: '#0f172a', fontWeight: 600}}>
                                    {m.data ? fmtData(m.data) : ''}
                                    {m.origem
                                        ? ` · ${m.origem}`
                                        : m.municipio
                                        ? ` · ${m.municipio}/${m.uf || ''}`
                                        : ''}
                                    {m.link && (
                                        <>
                                            {' '}
                                            <a href={m.link} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                                                {m.origem ? 'matéria ↗' : 'diário ↗'}
                                            </a>
                                        </>
                                    )}
                                </div>
                                {m.trecho && <div style={{fontSize: 13, color: '#475569', marginTop: 2}}>{m.trecho}</div>}
                            </div>
                        ))}
                    </>
                ) : (
                    <div style={{color: '#64748b', fontSize: 14}}>
                        Nenhuma menção localizada nos diários cobertos — confira manualmente nos links acima.
                    </div>
                )}
                <div style={fonteNota}>
                    Cobertura parcial (nem todo município/órgão está coberto) — a ausência de menção não prova nada.
                </div>
            </div>
        </div>
    )
}

function SecaoMidia({radar}: {radar: RadarFornecedor}) {
    const ms = radar.mencoes_midia || []
    if (!radar.midia_verificada && ms.length === 0) return null
    return (
        <div style={card}>
            <h3 style={{marginTop: 0, color: VERDE_ESCURO}}>
                Reputação na mídia <span style={countStyle}>(informativo · {ms.length})</span>
            </h3>
            {ms.length ? (
                <>
                    <div style={avisoHomonimo}>
                        ⚠️ Informativo — <b>não afeta o veredito</b>. Resultados por nome, podem incluir homônimos.
                    </div>
                    {ms.slice(0, 12).map((m: MencaoMidia, i) => {
                        const s = SENT_UI[m.sentimento || 'NEUTRO'] || SENT_UI.NEUTRO
                        return (
                            <div key={i} style={{fontSize: 14, marginBottom: 6}}>
                                <span title={s.rotulo}>{s.emoji}</span> <b style={{color: '#0f172a'}}>{m.veiculo}</b>
                                {m.data ? <span style={{color: '#94a3b8'}}> · {fmtData(m.data)}</span> : ''} ·{' '}
                                {m.url ? (
                                    <a href={m.url} target="_blank" rel="noopener noreferrer" style={{color: s.cor, textDecoration: 'none'}}>
                                        {m.titulo || '(sem título)'} ↗
                                    </a>
                                ) : (
                                    m.titulo || ''
                                )}
                            </div>
                        )
                    })}
                    <div style={fonteNota}>
                        Tom (🔻 negativo / 🔺 positivo / • neutro) é orientativo, por palavra-chave no título.
                    </div>
                </>
            ) : (
                <div style={{color: '#64748b', fontSize: 14}}>Nenhuma matéria recente localizada para este nome.</div>
            )}
        </div>
    )
}

function Certidoes() {
    return (
        <div style={card}>
            <h3 style={{marginTop: 0, color: VERDE_ESCURO}}>Regularidade (certidões)</h3>
            <div style={{fontSize: 13, color: '#64748b', marginBottom: 12}}>
                Certidões exigidas na habilitação (art. 68 da Lei 14.133). Abra o portal oficial e
                informe o CNPJ para emitir/conferir.
            </div>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10}}>
                {CERTIDOES.map((c) => (
                    <a
                        key={c.nome}
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{display: 'block', padding: '12px 14px', border: `1px solid ${VERDE}`, borderRadius: 10, textDecoration: 'none'}}
                    >
                        <div style={{fontWeight: 700, color: VERDE_ESCURO}}>{c.nome} ↗</div>
                        <div style={{fontSize: 12, color: '#64748b'}}>{c.desc}</div>
                    </a>
                ))}
            </div>
        </div>
    )
}

function baixarContratosCSV(contratos: ContratacaoFornecedor[], nome: string) {
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`
    const linhas = [
        ['Data', 'Órgão', 'UF', 'Objeto', 'Valor', 'Link PNCP'].join(';'),
        ...contratos.map((c) =>
            [c.data || '', c.orgao_nome || '', c.uf || '', c.objeto || '', c.valor ?? '', c.link || ''].map(esc).join(';')
        ),
    ].join('\r\n')
    const blob = new Blob(['﻿' + linhas], {type: 'text/csv;charset=utf-8'}) // BOM → Excel lê UTF-8
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `contratos-${(nome || 'fornecedor').slice(0, 40).replace(/[^\w\- ]/g, '')}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
}

function SecaoContratos({radar}: {radar: RadarFornecedor}) {
    const [modo, setModo] = useState<'orgao' | 'lista'>('orgao')
    const contratos = radar.contratos || []

    const grupos = useMemo(() => {
        const map = new Map<string, {orgao: string; uf?: string; contratos: ContratacaoFornecedor[]; total: number}>()
        for (const c of contratos) {
            const key = c.orgao_cnpj || c.orgao_nome || '—'
            let g = map.get(key)
            if (!g) {
                g = {orgao: c.orgao_nome || '—', uf: c.uf || undefined, contratos: [], total: 0}
                map.set(key, g)
            }
            g.contratos.push(c)
            g.total += c.valor || 0
        }
        return Array.from(map.values()).sort((a, b) => b.total - a.total)
    }, [contratos])

    if (contratos.length === 0) return null

    return (
        <div style={card}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12}}>
                <h3 style={{margin: 0, color: VERDE_ESCURO}}>
                    Contratos <span style={{color: '#94a3b8', fontSize: 14}}>({contratos.length})</span>
                </h3>
                <div style={{display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'}}>
                    <Button
                        onClick={() => baixarContratosCSV(contratos, radar.razao_social || radar.cnpj)}
                        variant="text"
                        size="small"
                        style={{textTransform: 'none', color: VERDE_ESCURO, minWidth: 0}}
                    >
                        ⬇ CSV
                    </Button>
                    <div style={{display: 'flex', border: `1px solid ${VERDE}`, borderRadius: 999, overflow: 'hidden'}}>
                        <Aba ativo={modo === 'orgao'} onClick={() => setModo('orgao')}>Por órgão</Aba>
                        <Aba ativo={modo === 'lista'} onClick={() => setModo('lista')}>Lista completa</Aba>
                    </div>
                </div>
            </div>
            {modo === 'orgao' ? (
                <>
                    <div style={{fontSize: 13, color: '#94a3b8', marginBottom: 8}}>
                        Clique num órgão para ver objeto e valor de cada contrato.
                    </div>
                    {grupos.map((g, i) => (
                        <OrgaoExpandivel key={i} grupo={g} />
                    ))}
                </>
            ) : (
                <ListaContratos contratos={contratos} />
            )}
        </div>
    )
}

function Aba({ativo, onClick, children}: {ativo: boolean; onClick: () => void; children: ReactNode}) {
    return (
        <button
            onClick={onClick}
            aria-pressed={ativo}
            style={{
                ...botaoBase,
                padding: '6px 16px',
                fontSize: 13,
                fontWeight: 700,
                background: ativo ? VERDE_ESCURO : 'transparent',
                color: ativo ? '#fff' : VERDE_ESCURO,
            }}
        >
            {children}
        </button>
    )
}

function OrgaoExpandivel({grupo}: {grupo: {orgao: string; uf?: string; contratos: ContratacaoFornecedor[]; total: number}}) {
    const [aberto, setAberto] = useState(false)
    return (
        <div style={{border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 8, overflow: 'hidden'}}>
            <button
                onClick={() => setAberto((a) => !a)}
                aria-expanded={aberto}
                style={{
                    ...botaoBase,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 14px',
                    background: aberto ? '#f8fafc' : '#fff',
                }}
            >
                <span style={{fontWeight: 600, color: '#0f172a', textAlign: 'left'}}>
                    <span style={{color: VERDE_ESCURO, marginRight: 8}}>{aberto ? '▾' : '▸'}</span>
                    {grupo.orgao}
                    {grupo.uf ? <span style={{color: '#94a3b8'}}> · {grupo.uf}</span> : ''}
                </span>
                <span style={{textAlign: 'right', whiteSpace: 'nowrap'}}>
                    <b style={{color: '#0f172a'}}>{brl(grupo.total)}</b>{' '}
                    <span style={{color: '#94a3b8', fontSize: 13}}>· {grupo.contratos.length} contrato(s)</span>
                </span>
            </button>
            <Collapse in={aberto}>
                <div style={{padding: '4px 14px 12px'}}>
                    {grupo.contratos.map((c, i) => (
                        <div
                            key={i}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 12,
                                padding: '8px 0',
                                borderTop: '1px solid #f1f5f9',
                                fontSize: 13,
                            }}
                        >
                            <div style={{color: '#475569'}}>
                                <span style={{color: '#94a3b8'}}>{fmtData(c.data)}</span> ·{' '}
                                {c.objeto || 'Objeto não informado'}
                                {c.link && (
                                    <>
                                        {' '}
                                        <a href={c.link} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                                            ver no PNCP ↗
                                        </a>
                                    </>
                                )}
                            </div>
                            <b style={{whiteSpace: 'nowrap', color: '#0f172a'}}>{brl(c.valor)}</b>
                        </div>
                    ))}
                </div>
            </Collapse>
        </div>
    )
}

function ListaContratos({contratos}: {contratos: ContratacaoFornecedor[]}) {
    const [pagina, setPagina] = useState(1)
    const [ordem, setOrdem] = useState<'data' | 'valor' | 'orgao'>('data')
    const [busca, setBusca] = useState('')

    const ordenados = useMemo(() => {
        const termo = busca.trim().toLowerCase()
        let arr = [...contratos]
        if (termo)
            arr = arr.filter((c) => `${c.orgao_nome || ''} ${c.objeto || ''}`.toLowerCase().includes(termo))
        if (ordem === 'valor') arr.sort((a, b) => (b.valor || 0) - (a.valor || 0))
        else if (ordem === 'orgao')
            arr.sort((a, b) => (a.orgao_nome || '').localeCompare(b.orgao_nome || '', 'pt-BR'))
        else arr.sort((a, b) => (b.data || '').localeCompare(a.data || ''))
        return arr
    }, [contratos, ordem, busca])

    const totalPaginas = Math.max(1, Math.ceil(ordenados.length / POR_PAGINA))
    const paginaSegura = Math.min(pagina, totalPaginas)
    const inicio = (paginaSegura - 1) * POR_PAGINA
    const itens = ordenados.slice(inicio, inicio + POR_PAGINA)
    return (
        <div>
            <div style={{display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap'}}>
                <TextField
                    size="small"
                    placeholder="Filtrar por órgão ou objeto…"
                    value={busca}
                    onChange={(e) => {
                        setBusca(e.target.value)
                        setPagina(1)
                    }}
                    style={{flex: 1, minWidth: 200}}
                />
                <TextField
                    select
                    size="small"
                    label="Ordenar por"
                    value={ordem}
                    onChange={(e) => {
                        setOrdem(e.target.value as 'data' | 'valor' | 'orgao')
                        setPagina(1)
                    }}
                    style={{width: 200}}
                >
                    <MenuItem value="data">Data (mais recente)</MenuItem>
                    <MenuItem value="valor">Valor (maior)</MenuItem>
                    <MenuItem value="orgao">Órgão (A–Z)</MenuItem>
                </TextField>
            </div>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 13}}>
                <thead>
                    <tr style={{textAlign: 'left', color: '#64748b'}}>
                        <th style={th}>Data</th>
                        <th style={th}>Órgão</th>
                        <th style={th}>Objeto</th>
                        <th style={{...th, textAlign: 'right'}}>Valor</th>
                        <th style={th}>PNCP</th>
                    </tr>
                </thead>
                <tbody>
                    {itens.map((c, i) => (
                        <tr key={i}>
                            <td style={{...td, whiteSpace: 'nowrap'}}>{fmtData(c.data)}</td>
                            <td style={td}>
                                {c.orgao_nome || '—'}
                                {c.uf ? <span style={{color: '#94a3b8'}}> · {c.uf}</span> : ''}
                            </td>
                            <td style={td}>{c.objeto || '—'}</td>
                            <td style={{...td, textAlign: 'right', whiteSpace: 'nowrap'}}>{brl(c.valor)}</td>
                            <td style={td}>
                                {c.link ? (
                                    <a href={c.link} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                                        abrir ↗
                                    </a>
                                ) : (
                                    '—'
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {totalPaginas > 1 && (
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14, marginTop: 16}}>
                    <Button size="small" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)} style={{textTransform: 'none', color: VERDE_ESCURO}}>
                        ← Anterior
                    </Button>
                    <span style={{color: '#475569', fontSize: 14}}>
                        Página {pagina} de {totalPaginas}
                    </span>
                    <Button size="small" disabled={pagina >= totalPaginas} onClick={() => setPagina((p) => p + 1)} style={{textTransform: 'none', color: VERDE_ESCURO}}>
                        Próxima →
                    </Button>
                </div>
            )}
        </div>
    )
}

function Metric({label, value}: {label: string; value: string}) {
    return (
        <div>
            <div style={{fontSize: 24, fontWeight: 800, color: '#0f172a'}}>{value}</div>
            <div style={{fontSize: 12, color: '#64748b'}}>{label}</div>
        </div>
    )
}

const th: CSSProperties = {padding: 8, borderBottom: '1px solid #e2e8f0'}
const td: CSSProperties = {padding: 8, borderBottom: '1px solid #f1f5f9'}
const countStyle: CSSProperties = {color: '#94a3b8', fontSize: 14, fontWeight: 400}
const avisoHomonimo: CSSProperties = {
    fontSize: 12,
    color: '#92400e',
    background: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: 8,
    padding: '7px 11px',
    marginBottom: 12,
}
const fonteNota: CSSProperties = {fontSize: 12, color: '#94a3b8', marginTop: 8}
const botaoBase: CSSProperties = {border: 'none', cursor: 'pointer', font: 'inherit'}
const botaoLinha: CSSProperties = {
    ...botaoBase,
    width: '100%',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    background: '#fff',
    transition: 'all 0.15s',
}
const backBtn: CSSProperties = {
    ...botaoBase,
    marginTop: 18,
    marginBottom: 4,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: '#fff',
    border: `1px solid ${VERDE}`,
    color: VERDE_ESCURO,
    borderRadius: 999,
    padding: '8px 18px',
    fontWeight: 700,
    fontSize: 14,
}
